import { v1TelemetryControllerTrack } from '~/generated/api'

export interface AnalyticsEventParams {
  item_name: string

  // Domain-specific hierarchy - captures the full nested structure
  risk_value_chain_name: string
  project_name: string
  area_name: string
  risk_name: string
  scenario_name: string
  solution_name: string

  /**
   * Generic numeric value 1. Meaning depends on the event (e.g. Risk Impact, Added Count).
   */
  value_metric_1: string | number
  value_metric_name_1: string
  /**
   * Generic numeric value 2. Meaning depends on the event (e.g. Risk Probability, Removed Count).
   */
  value_metric_2: string | number
  value_metric_name_2: string
  status: string
  [key: string]: any
}

export function trackEvent(
  event: string,
  properties?: Record<string, unknown>,
) {
  void v1TelemetryControllerTrack({
    body: {
      event,
      ...(properties && { properties }),
    },
  }).catch(() => {})
}

export class AnalyticsLogger {
  private static sanitizeString(
    value: string | undefined | null | number,
  ): string {
    return value?.toString() || ''
  }

  static send(
    eventName: string,
    params?: Partial<AnalyticsEventParams> | undefined,
  ) {
    const { selectedProject, isConfidential } = storeToRefs(useProjectStore())
    const confidentialProject = Boolean(
      selectedProject.value?.isConfidential || isConfidential.value,
    )
    if (confidentialProject) {
      return
    }

    const { gtag } = useGtag()
    if (gtag) {
      const finalParams: AnalyticsEventParams = {
        ...useAdvisorCountryParams(),
        item_name: this.sanitizeString(params?.item_name),
        risk_value_chain_name: this.sanitizeString(
          params?.risk_value_chain_name,
        ),
        project_name: this.sanitizeString(params?.project_name),
        area_name: this.sanitizeString(params?.area_name),
        risk_name: this.sanitizeString(params?.risk_name),
        scenario_name: this.sanitizeString(params?.scenario_name),
        solution_name: this.sanitizeString(params?.solution_name),
        value_metric_1: this.sanitizeString(params?.value_metric_1),
        value_metric_name_1: this.sanitizeString(params?.value_metric_name_1),
        value_metric_2: this.sanitizeString(params?.value_metric_2),
        value_metric_name_2: this.sanitizeString(params?.value_metric_name_2),
        status: this.sanitizeString(params?.status),
      }
      applyAnalyticsUserId(gtag)
      gtag('event', eventName, finalParams)
    }
    else {
      console.warn('Google Analytics not initialized')
    }
  }
}

export type PdfExportReportType = 'Report' | 'Summary' | 'Web Report'

export function trackPdfExportButtonClicked(
  reportType: PdfExportReportType,
  params?: Partial<AnalyticsEventParams>,
) {
  AnalyticsLogger.send('pdf_export_button_clicked', {
    ...params,
    status: reportType,
  })
}

/**
 * How the published web report was viewed.
 * - `Preview`: in-app preview tab (advisor checking how it looks).
 * - `Link`: public access-token link (very likely the customer opening the
 *   final published version that was sent to them).
 */
export type PublishedReportViewSource = 'Preview' | 'Link'

export function trackPublishedReportOpened(
  source: PublishedReportViewSource,
  params?: Partial<AnalyticsEventParams>,
) {
  AnalyticsLogger.send('published_report_opened', {
    ...params,
    status: source,
  })
}

/**
 * Why opening a published report link failed. `expired`/`revoked` mean the
 * recipient tried to open the link after it lapsed — interesting signal that
 * the report is still wanted.
 */
export type PublishedReportOpenFailReason =
  | 'expired'
  | 'revoked'
  | 'not_found'
  | 'unknown'

export function trackPublishedReportOpenFailed(
  reason: PublishedReportOpenFailReason,
  params?: Partial<AnalyticsEventParams>,
) {
  AnalyticsLogger.send('published_report_open_failed', {
    ...params,
    status: reason,
  })
}
